/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useRef, type MouseEvent, type KeyboardEvent } from 'react';
import styles from './styles.module.scss';
import { View } from '@az/starc-ui';
import { deleteCookie } from '@/utils/cookie';
import { cookieConstant } from '@/constants/cookies';
import { useRouter } from 'next/navigation';
import RecentSearchItem from './RecentSearchItem';
import { useLabel } from '@/hooks/useLabels';
import { sessionStorage } from '../../utils/sessionStorage';
import { ONLYSPACES } from '@/utils/validatorRegex';
import { globalConstant } from '@/constants/global';
import { routePaths } from '@/constants/routePaths';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';

const isKeyboardEvent = (e: React.KeyboardEvent | React.MouseEvent): e is React.KeyboardEvent => {
  return (e as React.KeyboardEvent).key !== undefined;
};

type RecentSearchesProps = {
  recentSearches: string[];
  searchText: string;
  cursor: number;
  addRecentSearch: (searchTerm: string) => void;
  deleteRecentSearch: (index: number) => void;
  onSearchTextChange: (searchTerm: string) => void;
  handleClose: () => void;
  onClearClick: () => void;
  magnifierButtonRef?: React.MutableRefObject<HTMLButtonElement | null>;
};

const RecentSearches = ({
  recentSearches,
  searchText,
  cursor,
  addRecentSearch,
  deleteRecentSearch,
  onSearchTextChange,
  handleClose,
  onClearClick,
  magnifierButtonRef,
}: RecentSearchesProps) => {
  const deleteEntryButtonRef1 = useRef<HTMLButtonElement>(null);
  const deleteEntryButtonRef2 = useRef<HTMLButtonElement>(null);
  const deleteEntryButtonRef3 = useRef<HTMLButtonElement>(null);
  const deleteEntryButtonRefs = [
    deleteEntryButtonRef1,
    deleteEntryButtonRef2,
    deleteEntryButtonRef3,
  ];
  const router = useRouter();
  const lblRecentSearches = useLabel('label_recentSearches');
  const locale = useLocale();
  const isMx = locale === countryCodes.mx;

  const handleEntryClick = (e: MouseEvent | KeyboardEvent, entry: string) => {
    const checkGiftCardKeyword = entry.toLowerCase().replace(ONLYSPACES, '');
    const isGiftcardSearch =
      checkGiftCardKeyword === globalConstant.giftcard ||
      checkGiftCardKeyword === globalConstant.giftcards;
    if (
      deleteEntryButtonRefs.some((ref) => ref.current === e.target) ||
      (isKeyboardEvent(e) && e.code !== 'Enter' && e.code !== 'Space')
    ) {
      return;
    }

    if (isGiftcardSearch) {
      handleClose();
      onClearClick();
      void router.push(isMx ? routePaths.blankURL : routePaths.giftCardLanding);
    } else {
      // Clear the filterOpenStates cookie before navigating
      void deleteCookie(cookieConstant.names.filterOpenStates);
      void router.push(`/searchresult?searchText=${encodeURIComponent(entry)}`);
      sessionStorage.setItem('searchMethod', 'Most Recent');
      sessionStorage.setItem('performedSearch', 'true');
      // Set flag to trigger vehicle auto-add from search
      sessionStorage.setItem('searchButtonPressed', 'true');
      onSearchTextChange(entry);
      addRecentSearch(entry);
      handleClose();
    }
  };

  const handleDeleteClick = (e: MouseEvent | KeyboardEvent, index: number) => {
    deleteRecentSearch(index);
    magnifierButtonRef?.current?.focus();
  };

  return (
    <View as="section" className={styles.recentSearchesContainer}>
      <View padding={[0, 4]}>
        <span className={styles.header}>{lblRecentSearches}</span>
      </View>
      <View as="ul">
        {recentSearches.map((entry, index) => (
          <RecentSearchItem
            key={`recent-${entry}`}
            handleDeleteClick={handleDeleteClick}
            handleEntryClick={handleEntryClick}
            searchText={searchText}
            entry={entry}
            index={index}
            cursor={cursor}
            deleteEntryButtonRef={deleteEntryButtonRefs[index]}
          />
        ))}
      </View>
    </View>
  );
};

export default RecentSearches;
